import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, fromEvent, Subject } from 'rxjs';
import { RGBA } from 'color-blend/dist/types';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { isDefined, isString } from '../utils';
import { IDWallMedia } from '../models';
import { WatchdogService } from './watchdog.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WebSocketResponse } from '../models/websocket.model';

export interface BatteryLevel {
  level: number;
  charging: boolean;
}

type IntercomMethods = {
  init: undefined;
  'device.turnOff': undefined;
  'device.work_schedule_periods': unknown;
  'device.work_schedule_periods_v2': Array<{
    id: number;
    active: boolean;
    startDay: number | null;
    startTime: string | null;
    endDay: number | null;
    endTime: string | null;
  }>;
  'playlist.get_items': undefined;
  'playlist.get_playing_now': undefined;
  'playlist.stop': undefined;
  'playlist.resume': undefined;
  'ambient_light.on': {
    color: RGBA
  };
  'ambient_light.off': undefined;
  'battery.level.get': undefined;
  'brightness.get': undefined;
  'brightness.set': number;
};

export type IntercomMethod = keyof IntercomMethods;
export type IntercomMethodData<T extends IntercomMethod = IntercomMethod> = IntercomMethods[T];
export type IntercomMethodDataArgs<
  TMethod extends IntercomMethod,
  TData extends IntercomMethodData<TMethod> = IntercomMethodData<TMethod>
> =
  TData extends undefined
    ? []
    : [IntercomMethodData<TMethod>];

export type IIntercomMethodMessageBase<
  TMethod extends IntercomMethod = IntercomMethod,
  TData extends IntercomMethodData<TMethod> = IntercomMethodData<TMethod>
> = {
  type: 'dwall-events';
  version: string;
  method: TMethod;
  data: TData;
};

export type IIntercomMethodMessage = {
  [K in IntercomMethod]: IIntercomMethodMessageBase<K, IntercomMethodData<K>>;
}[IntercomMethod];

type IntercomEvents = {
  'device.signature': string;
  'device.work_schedule_periods_v2.failed': undefined;
  'playlist.items': ReadonlyArray<IDWallMedia>;
  'playlist.playing_now': IDWallMedia;
  'playlist.playing_finished': IDWallMedia;
  'battery.level': BatteryLevel;
  'brightness.level': number;
  'demo.events': WebSocketResponse;
};

export type IntercomEvent = keyof IntercomEvents;
export type IntercomEventData<T extends IntercomEvent = IntercomEvent> = IntercomEvents[T];
export type IntercomEventDataArgs<
  TEvent extends IntercomEvent,
  TData extends IntercomEventData<TEvent> = IntercomEventData<TEvent>
> =
  TData extends undefined
    ? []
    : [IntercomEventData<TEvent>];

export type IIntercomEventMessageBase<
  TEvent extends IntercomEvent = IntercomEvent,
  TData extends IntercomEventData<TEvent> = IntercomEventData<TEvent>
> = {
  type: 'dwall-events';
  version: string;
  event: TEvent;
  data: TData;
}

export type IIntercomEventMessage = {
  [K in IntercomEvent]: IIntercomEventMessageBase<K, IntercomEventData<K>>;
}[IntercomEvent];

@Injectable()
export class Intercom {
  private readonly destroyRef = inject(DestroyRef);
  private readonly watchdog = inject(WatchdogService);

  private readonly logger = this.watchdog.tag('Intercom', 'cyan');

  private readonly portSubject = new BehaviorSubject<MessagePort | null>(null);
  private readonly connectionCountSubject = new BehaviorSubject<number>(0);
  private readonly lastConnectionAtSubject = new BehaviorSubject<Date | null>(null);
  private readonly messageSubject = new Subject<IIntercomEventMessage>();

  private readonly deviceSignatureSubject = new BehaviorSubject<string | null>(null);
  private readonly batteryLevelSubject = new BehaviorSubject<BatteryLevel | null>(null);
  private readonly currentMediaSubject = new BehaviorSubject<IDWallMedia | null>(null);

  public readonly connected$ = this.portSubject.pipe(
    map((port) => !!port),
  );

  public readonly connectionCount$ = this.connectionCountSubject.asObservable();
  public readonly lastConnectionAt$ = this.lastConnectionAtSubject.asObservable();

  public readonly messages$ = this.messageSubject.asObservable();

  public readonly deviceSignature$ = this.deviceSignatureSubject.asObservable();
  public readonly currentMedia$ = this.currentMediaSubject.asObservable();
  public readonly batteryLevel$ = this.batteryLevelSubject.asObservable();

  public get deviceSignature() {
    return this.deviceSignatureSubject.getValue();
  }

  public initialize(): void {
    this.logger.info('Initialize');

    // Handshake
    fromEvent<MessageEvent>(window, 'message').pipe(
      filter((event): event is MessageEvent<'dwall-handshake'> => {
        return event.data === 'dwall-handshake' && isDefined(event.ports) && isDefined(event.ports[0]);
      }),
      tap((event) => {
        this.logger.debug('Handshake', JSON.stringify(event));
      }),
      map((event) => event.ports[0]),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((port) => {
      this.portSubject.next(port);
      this.connectionCountSubject.next(this.connectionCountSubject.value + 1);
      this.lastConnectionAtSubject.next(new Date());

      // Initialization call
      this.call('init');
      this.call('battery.level.get');
      this.call('playlist.get_playing_now');
    });

    // Message handling
    this.portSubject.asObservable().pipe(
      switchMap((port) => {
        if (!port) {
          return EMPTY;
        }

        port.start();

        return fromEvent<MessageEvent>(port, 'message');
      }),
      map((event) => {
        // TODO: Remove this hack when Android will be fixed
        if (isString(event.data)) {
          // @eslint-disable-next-line
          return JSON.parse(event.data.replace('\/', ''));
        }

        return event.data;
      }),
      filter((message) => {
        return message.type && message.version && message.event;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((message) => {
      this.messageSubject.next(message);
    });

    // Device DWall ID
    this.messages$.pipe(
      filter((message) => message.event === 'device.signature'),
      map((message) => message.data),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((id) => {
      this.deviceSignatureSubject.next(id);
    });

    // Current media playing
    this.messages$.pipe(
      filter((message) => message.event === 'playlist.playing_now'),
      map((message) => message.data),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.currentMediaSubject.next(data);
    });

    // Battery level updates
    this.messages$.pipe(
      filter((message) => message.event === 'battery.level'),
      map((message) => message.data),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((status) => {
      this.batteryLevelSubject.next(status);
    });
  }

  public call<TMethod extends IntercomMethod = IntercomMethod>(
    method: TMethod,
    ...args: IntercomMethodDataArgs<TMethod>
  ) {
    const port = this.portSubject.getValue();

    if (!port) {
      return false;
    }

    const message = {
      type: 'dwall-events',
      version: '0.0.1',
      method,
      data: (
        args.length > 0 ? args[0] : undefined
      ) as IntercomMethodData<TMethod>,
    };

    // @TODO: Remove this hack when Android will be fixed
    port.postMessage(JSON.stringify(message));
    this.logger.debug('Sent:', message);

    return true;
  }

}
